// ============================================================================
// GERENCIAMENTO REVALIDA PRO — DASHBOARD ADMIN
// ============================================================================
// Arquivo: src/components/Dashboard_ADM/GerenciamentoRevalida.tsx
// UPDATED: 2026-04-16 — v2.0
// ✅ Botão de editar ao lado do "olhinho" na tabela
// ✅ Modal de edição completo (espelha o formulário público RevalidaPro)
// ✅ PATCH /revalida/:id para salvar edições
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, Filter, RefreshCw, Download, Eye, CheckCircle,
  XCircle, Clock, AlertTriangle, Loader2, X, MapPin,
  Mail, Phone, User, FileText, Hash, Calendar, Instagram,
  ExternalLink, MessageSquare, Trash2, Image, Edit,
} from 'lucide-react';
import api from '../../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────
type RevalidaStatus = 'pending' | 'approved' | 'rejected';
type ModalTab = 'info' | 'documentos';

interface Revalida {
  id: string;
  systemId: string;
  status: RevalidaStatus;
  nomeCompleto: string;
  dataNascimento?: string;
  cpf: string;
  cnpj?: string;
  email: string;
  telefone?: string;
  cep?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  cidade?: string;
  estado?: string;
  historicoProfissional?: string;
  redesSociais?: string;
  createdAt: string;
  reviewedAt?: string;
  rejectionReason?: string;
}

interface Documento {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  documentType: string;
  description?: string;
  url: string;
  uploadedAt: string;
}

interface RevalidaStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

interface FormEdit {
  nomeCompleto: string;
  dataNascimento: string;
  cpf: string;
  cnpj: string;
  email: string;
  telefone: string;
  cep: string;
  endereco: string;
  numero: string;
  complemento: string;
  cidade: string;
  estado: string;
  historicoProfissional: string;
  redesSociais: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const UFS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS',
  'MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

const FORM_EDIT_INITIAL: FormEdit = {
  nomeCompleto: '', dataNascimento: '', cpf: '', cnpj: '',
  email: '', telefone: '', cep: '', endereco: '', numero: '',
  complemento: '', cidade: '', estado: '', historicoProfissional: '', redesSociais: '',
};

const getErrorMessage = (e: any, fallback = 'Erro'): string => {
  const d = e?.response?.data;
  if (Array.isArray(d?.message))      return d.message.join(' | ');
  if (typeof d?.message === 'string') return d.message;
  if (typeof e?.message === 'string') return e.message;
  return fallback;
};
const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  const [datePart] = iso.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day); // construtor local — sem interpretar como UTC
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtCPF = (cpf: string) =>
  cpf?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') ?? cpf;

const fmtFileSize = (bytes: number) => {
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImage = (mime: string) => mime.startsWith('image/');

const DOC_TYPE_LABEL: Record<string, string> = {
  cpf: 'CPF', rg: 'RG / Identidade', comprovante: 'Comprovante de Endereço',
  diploma: 'Diploma', certificado: 'Certificado', foto: 'Foto',
  homologacao: 'Declaração de Experiência', outro: 'Outro',
};

const inputCls =
  'w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:outline-none ' +
  'focus:ring-2 focus:ring-amber-500 focus:border-amber-400 focus:bg-white ' +
  'text-gray-900 text-sm font-medium transition-all';

const labelCls = 'block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5';

// ─── StatusBadge ─────────────────────────────────────────────────────────────
const StatusBadge: React.FC<{ status: RevalidaStatus }> = ({ status }) => {
  const cfg = {
    pending:  { bg:'bg-amber-100',  text:'text-amber-800',  border:'border-amber-300',  icon:Clock,       label:'Pendente'  },
    approved: { bg:'bg-green-100',  text:'text-green-800',  border:'border-green-300',  icon:CheckCircle, label:'Aprovado'  },
    rejected: { bg:'bg-red-100',    text:'text-red-800',    border:'border-red-300',    icon:XCircle,     label:'Rejeitado' },
  }[status];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      <Icon className="w-3.5 h-3.5" />{cfg.label}
    </span>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────
const GerenciamentoRevalida: React.FC = () => {
  const [registros, setRegistros]         = useState<Revalida[]>([]);
  const [stats, setStats]                 = useState<RevalidaStats | null>(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [busca, setBusca]                 = useState('');
  const [buscaInput, setBuscaInput]       = useState('');
  const [filtroStatus, setFiltroStatus]   = useState<string>('todos');
  const [selecionado, setSelecionado]     = useState<Revalida | null>(null);
  const [modalVer, setModalVer]           = useState(false);
  const [modalTab, setModalTab]           = useState<ModalTab>('info');
  const [modalRejeitar, setModalRejeitar] = useState<Revalida | null>(null);
  const [motivo, setMotivo]               = useState('');
  const [salvando, setSalvando]           = useState(false);

  // Documentos
  const [documentos, setDocumentos]       = useState<Documento[]>([]);
  const [loadingDocs, setLoadingDocs]     = useState(false);

  // Edição
  const [modalEditar, setModalEditar]     = useState<Revalida | null>(null);
  const [formEdit, setFormEdit]           = useState<FormEdit>(FORM_EDIT_INITIAL);
  const [erroEdit, setErroEdit]           = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setBusca(buscaInput), 400);
    return () => clearTimeout(t);
  }, [buscaInput]);

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const fetchRegistros = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data: any = await api.get('/revalida?page=1&limit=200');
      setRegistros(data.registrations ?? []);
    } catch (e: any) {
      setError(getErrorMessage(e, 'Erro ao carregar solicitações'));
    } finally { setLoading(false); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const data: any = await api.get('/revalida/stats/summary');
      setStats({ total:data.total??0, pending:data.pending??0, approved:data.approved??0, rejected:data.rejected??0 });
    } catch { /* opcional */ }
  }, []);

  const fetchDocumentos = useCallback(async (id: string) => {
    setLoadingDocs(true); setDocumentos([]);
    try {
      const data: any = await api.get(`/documents/affiliate/${id}`);
      setDocumentos(Array.isArray(data) ? data : []);
    } catch { setDocumentos([]); }
    finally { setLoadingDocs(false); }
  }, []);

  useEffect(() => { fetchRegistros(); fetchStats(); }, [fetchRegistros, fetchStats]);

  const abrirModal = (r: Revalida) => {
    setSelecionado(r);
    setModalTab('info');
    setModalVer(true);
    fetchDocumentos(r.id);
  };

  // ─── Abrir edição ─────────────────────────────────────────────────────────
  const abrirEditar = (r: Revalida) => {
    setFormEdit({
      nomeCompleto:          r.nomeCompleto          ?? '',
      dataNascimento:        r.dataNascimento ? r.dataNascimento.slice(0, 10) : '',
      cpf:                   r.cpf?.replace(/\D/g, '') ?? '',
      cnpj:                  r.cnpj?.replace(/\D/g, '') ?? '',
      email:                 r.email                 ?? '',
      telefone:              r.telefone              ?? '',
      cep:                   r.cep?.replace(/\D/g, '') ?? '',
      endereco:              r.endereco              ?? '',
      numero:                r.numero                ?? '',
      complemento:           r.complemento           ?? '',
      cidade:                r.cidade                ?? '',
      estado:                r.estado                ?? '',
      historicoProfissional: r.historicoProfissional ?? '',
      redesSociais:          r.redesSociais          ?? '',
    });
    setErroEdit(null);
    setModalEditar(r);
    setModalVer(false);
  };

  const handleEditChange =
    (key: keyof FormEdit) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setFormEdit(prev => ({ ...prev, [key]: e.target.value }));

  // ─── Salvar edição ────────────────────────────────────────────────────────
  const handleSalvarEdicao = async () => {
    if (!modalEditar) return;
    setSalvando(true); setErroEdit(null);
    try {
      const payload: Record<string, any> = {
        nomeCompleto:          formEdit.nomeCompleto.trim()          || undefined,
        dataNascimento:        formEdit.dataNascimento               || undefined,
        cpf:                   formEdit.cpf.replace(/\D/g, '')      || undefined,
        cnpj:                  formEdit.cnpj.replace(/\D/g, '')     || undefined,
        email:                 formEdit.email.trim()                 || undefined,
        telefone:              formEdit.telefone.trim()              || undefined,
        cep:                   formEdit.cep.replace(/\D/g, '')      || undefined,
        endereco:              formEdit.endereco.trim()              || undefined,
        numero:                formEdit.numero.trim()                || undefined,
        complemento:           formEdit.complemento.trim()           || undefined,
        cidade:                formEdit.cidade.trim()                || undefined,
        estado:                formEdit.estado                       || undefined,
        historicoProfissional: formEdit.historicoProfissional.trim() || undefined,
        redesSociais:          formEdit.redesSociais.trim()          || undefined,
      };
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
      await api.patch(`/revalida/${modalEditar.id}`, payload);
      setModalEditar(null);
      await fetchRegistros();
      await fetchStats();
    } catch (e: any) {
      setErroEdit(getErrorMessage(e, 'Erro ao salvar edição'));
    } finally { setSalvando(false); }
  };

  // ─── Aprovar ──────────────────────────────────────────────────────────────
  const handleAprovar = async (id: string) => {
    setSalvando(true);
    try {
      await api.post(`/revalida/${id}/approve`);
      await fetchRegistros(); await fetchStats();
      setSelecionado(prev => prev?.id === id ? { ...prev, status: 'approved' } : prev);
    } catch (e: any) { alert(getErrorMessage(e, 'Erro ao aprovar')); }
    finally { setSalvando(false); }
  };

  // ─── Rejeitar ─────────────────────────────────────────────────────────────
  const handleRejeitar = async () => {
    if (!modalRejeitar) return;
    if (!motivo.trim() || motivo.trim().length < 10) { alert('Informe o motivo (mínimo 10 caracteres)'); return; }
    setSalvando(true);
    try {
      await api.post(`/revalida/${modalRejeitar.id}/reject`, { reason: motivo });
      setModalRejeitar(null); setMotivo(''); setModalVer(false);
      await fetchRegistros(); await fetchStats();
    } catch (e: any) { alert(getErrorMessage(e, 'Erro ao rejeitar')); }
    finally { setSalvando(false); }
  };

  // ─── Excluir ──────────────────────────────────────────────────────────────
  const handleExcluir = async (r: Revalida) => {
    if (!window.confirm(`Excluir permanentemente a solicitação de "${r.nomeCompleto}"?\n\nEsta ação não pode ser desfeita.`)) return;
    setSalvando(true);
    try {
      await api.delete(`/revalida/${r.id}`);
      setModalVer(false);
      await fetchRegistros(); await fetchStats();
    } catch (e: any) { alert(getErrorMessage(e, 'Erro ao excluir')); }
    finally { setSalvando(false); }
  };

  // ─── WhatsApp ─────────────────────────────────────────────────────────────
  const handleWhatsApp = (r: Revalida) => {
    const num    = (r.telefone ?? '').replace(/\D/g, '');
    const numero = num.startsWith('55') ? num : `55${num}`;
    const msg    = encodeURIComponent(
      `Olá ${r.nomeCompleto}! 👋\n\n` +
      `Sua solicitação de *Revalida Pro* foi *aprovada* pela Federação! ✅\n\n` +
      `*Protocolo:* ${r.systemId}\n\n` +
      `Em breve nossa equipe entrará em contato com as próximas etapas.\n` +
      `Qualquer dúvida, estamos à disposição! 🎓`
    );
    window.open(`https://wa.me/${numero}?text=${msg}`, '_blank');
  };

  // ─── Exportar CSV ─────────────────────────────────────────────────────────
  const handleExportar = () => {
    const csv = [
      'Protocolo,Nome,Email,CPF,Telefone,Cidade,Estado,Status,Data',
      ...registros.map(r =>
        `${r.systemId},"${r.nomeCompleto}",${r.email},${fmtCPF(r.cpf)},${r.telefone??''},${r.cidade??''},${r.estado??''},${r.status},${fmtDate(r.createdAt)}`
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `revalida_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // ─── Filtros ──────────────────────────────────────────────────────────────
  const filtrados = useMemo(() => registros.filter(r => {
    if (filtroStatus !== 'todos' && r.status !== filtroStatus) return false;
    const q = busca.trim().toLowerCase();
    if (!q) return true;
    return (
      r.nomeCompleto.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      r.systemId.toLowerCase().includes(q) ||
      r.cpf.includes(q) ||
      (r.cidade ?? '').toLowerCase().includes(q)
    );
  }), [registros, filtroStatus, busca]);

  const statsCalc = useMemo(() => stats ?? {
    total:    registros.length,
    pending:  registros.filter(r => r.status === 'pending').length,
    approved: registros.filter(r => r.status === 'approved').length,
    rejected: registros.filter(r => r.status === 'rejected').length,
  }, [stats, registros]);

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* HEADER + STATS */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1">Revalida Pro</h2>
            <p className="text-gray-600">Solicitações de regularização profissional</p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => { fetchRegistros(); fetchStats(); }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-all">
              <RefreshCw className="w-4 h-4" /> Atualizar
            </button>
            <button onClick={handleExportar}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-all">
              <Download className="w-4 h-4" /> Exportar
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          {[
            { label:'Total',      value:statsCalc.total,    bg:'bg-blue-50',  border:'border-blue-200',  pulse:false },
            { label:'Pendentes',  value:statsCalc.pending,  bg:'bg-amber-50', border:'border-amber-200', pulse:statsCalc.pending > 0 },
            { label:'Aprovados',  value:statsCalc.approved, bg:'bg-green-50', border:'border-green-200', pulse:false },
            { label:'Rejeitados', value:statsCalc.rejected, bg:'bg-red-50',   border:'border-red-200',   pulse:false },
          ].map(s => (
            <div key={s.label} className={`relative ${s.bg} rounded-xl p-4 border ${s.border}`}>
              {s.pulse && <div className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />}
              <p className="text-sm text-gray-600 mb-1">{s.label}</p>
              <p className="text-2xl font-black text-gray-900">{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FILTROS */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input value={buscaInput} onChange={e => setBuscaInput(e.target.value)}
              placeholder="Buscar por nome, email, CPF, protocolo..."
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-900 font-semibold" />
          </div>
          <div className="flex items-center gap-3 bg-gray-50 border-2 border-gray-300 rounded-xl px-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
              className="bg-transparent outline-none py-3 text-gray-900 font-bold w-full">
              <option value="todos">Todos Status</option>
              <option value="pending">Pendentes</option>
              <option value="approved">Aprovados</option>
              <option value="rejected">Rejeitados</option>
            </select>
          </div>
        </div>
      </div>

      {/* TABELA */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-20 flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
            <p className="text-gray-500 font-semibold">Carregando solicitações...</p>
          </div>
        ) : error ? (
          <div className="py-20 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-600 font-bold">{error}</p>
            <button onClick={fetchRegistros} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-xl font-bold">Tentar novamente</button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {['Protocolo / Nome','Contato','Localização','Status','Data',''].map(h => (
                      <th key={h} className={`px-6 py-4 text-sm font-black text-gray-900 ${h===''?'text-right':'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filtrados.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-black text-base flex-shrink-0">
                            {r.nomeCompleto.charAt(0)}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{r.nomeCompleto}</p>
                            <p className="text-xs font-mono text-amber-600">{r.systemId}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <p className="text-sm text-gray-900 flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" />{r.email}</p>
                          {r.telefone && <p className="text-sm text-gray-600 flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" />{r.telefone}</p>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {r.cidade || r.estado
                          ? <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" /><span className="text-sm text-gray-900">{[r.cidade,r.estado].filter(Boolean).join(', ')}</span></div>
                          : <span className="text-gray-400 text-sm">—</span>}
                      </td>
                      <td className="px-6 py-4"><StatusBadge status={r.status} /></td>
                      <td className="px-6 py-4"><span className="text-sm text-gray-600">{fmtDate(r.createdAt)}</span></td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          {/* Ver */}
                          <button onClick={() => abrirModal(r)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Ver detalhes">
                            <Eye className="w-4 h-4" />
                          </button>
                          {/* ✅ EDITAR */}
                          <button onClick={() => abrirEditar(r)}
                            className="p-2 text-amber-500 hover:bg-amber-50 rounded-lg transition-colors" title="Editar">
                            <Edit className="w-4 h-4" />
                          </button>
                          {r.status === 'pending' && (<>
                            <button onClick={() => handleAprovar(r.id)} disabled={salvando}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50" title="Aprovar">
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button onClick={() => { setModalRejeitar(r); setMotivo(''); }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Rejeitar">
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>)}
                          <button onClick={() => handleExcluir(r)} disabled={salvando}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50" title="Excluir">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtrados.length === 0 && (
              <div className="py-12 text-center">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-semibold">Nenhuma solicitação encontrada</p>
              </div>
            )}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600">
                Mostrando <span className="font-bold text-gray-900">{filtrados.length}</span> de{' '}
                <span className="font-bold text-gray-900">{registros.length}</span> solicitações
              </p>
            </div>
          </>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          MODAL VER DETALHES
      ══════════════════════════════════════════════════════════════════ */}
      {modalVer && selecionado && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">

            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-amber-600 to-orange-600 rounded-t-2xl flex-shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-white/20 border-2 border-white/40 flex items-center justify-center text-white font-black text-2xl flex-shrink-0">
                    {selecionado.nomeCompleto.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-white mb-1">{selecionado.nomeCompleto}</h3>
                    <div className="flex items-center gap-2 bg-white/20 rounded-xl px-3 py-1 w-fit">
                      <Hash className="w-4 h-4 text-white" />
                      <span className="font-mono font-black text-white text-sm">{selecionado.systemId}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={selecionado.status} />
                  <button onClick={() => setModalVer(false)}
                    className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 mt-4">
                {([['info','Informações'],['documentos','Documentos']] as [ModalTab,string][]).map(([tab,label]) => (
                  <button key={tab} onClick={() => setModalTab(tab)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-colors
                      ${modalTab === tab ? 'bg-white text-amber-700' : 'bg-white/20 text-white hover:bg-white/30'}`}>
                    {label}
                    {tab === 'documentos' && documentos.length > 0 && (
                      <span className="ml-1.5 text-[10px] bg-white/30 px-1.5 py-0.5 rounded-full">
                        {documentos.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">

              {/* ABA INFORMAÇÕES */}
              {modalTab === 'info' && (<>
                <Section title="Dados Pessoais" icon={<User className="w-4 h-4" />}>
                  <Grid>
                    <Info label="Nome Completo"     value={selecionado.nomeCompleto} />
                    <Info label="CPF"               value={fmtCPF(selecionado.cpf)} mono />
                    {selecionado.cnpj && <Info label="CNPJ" value={selecionado.cnpj} mono />}
                    {selecionado.dataNascimento && <Info label="Data de Nascimento" value={fmtDate(selecionado.dataNascimento)} />}
                    <Info label="E-mail"            value={selecionado.email} />
                    <Info label="Telefone/WhatsApp" value={selecionado.telefone ?? '—'} />
                  </Grid>
                </Section>

                {(selecionado.cidade || selecionado.endereco) && (
                  <Section title="Endereço" icon={<MapPin className="w-4 h-4" />}>
                    <Grid>
                      <Info label="Endereço"  value={[selecionado.endereco, selecionado.numero].filter(Boolean).join(', ') || '—'} />
                      {selecionado.complemento && <Info label="Complemento" value={selecionado.complemento} />}
                      <Info label="CEP"       value={selecionado.cep ?? '—'} />
                      <Info label="Cidade/UF" value={[selecionado.cidade, selecionado.estado].filter(Boolean).join(' — ') || '—'} />
                    </Grid>
                  </Section>
                )}

                {selecionado.historicoProfissional && (
                  <Section title="Histórico Profissional" icon={<FileText className="w-4 h-4" />}>
                    <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                      {selecionado.historicoProfissional}
                    </div>
                  </Section>
                )}

                {selecionado.redesSociais && (
                  <Section title="Redes Sociais / Portfólio" icon={<Instagram className="w-4 h-4" />}>
                    <a href={selecionado.redesSociais.startsWith('http') ? selecionado.redesSociais : `https://${selecionado.redesSociais}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-sm font-semibold hover:bg-amber-100 transition-all">
                      <ExternalLink className="w-4 h-4" /> {selecionado.redesSociais}
                    </a>
                  </Section>
                )}

                {selecionado.status === 'rejected' && selecionado.rejectionReason && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                    <p className="text-xs font-bold text-red-500 uppercase tracking-wider mb-1">Motivo da Rejeição</p>
                    <p className="text-sm text-red-800">{selecionado.rejectionReason}</p>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  Solicitado em {fmtDate(selecionado.createdAt)}
                </div>
              </>)}

              {/* ABA DOCUMENTOS */}
              {modalTab === 'documentos' && (
                <div>
                  {loadingDocs ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                      <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                      <p className="text-sm text-gray-500">Carregando documentos...</p>
                    </div>
                  ) : documentos.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
                        <FileText className="w-7 h-7 text-gray-300" />
                      </div>
                      <p className="text-sm font-semibold text-gray-600">Nenhum documento enviado</p>
                      <p className="text-xs text-gray-400 mt-1">O solicitante ainda não enviou documentos.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-500">
                        {documentos.length} documento{documentos.length !== 1 ? 's' : ''} enviado{documentos.length !== 1 ? 's' : ''}
                      </p>
                      {documentos.map(doc => (
                        <div key={doc.id} className="border border-gray-200 rounded-xl overflow-hidden">
                          {isImage(doc.mimeType) && (
                            <div className="bg-gray-50 flex items-center justify-center p-4 border-b border-gray-100">
                              <img src={doc.url} alt={doc.fileName}
                                className="max-h-48 max-w-full object-contain rounded-lg"
                                onError={e => {
                                  const el = e.target as HTMLImageElement;
                                  el.parentElement!.innerHTML = '<p class="text-xs text-gray-400 py-4">Não foi possível carregar a imagem</p>';
                                }} />
                            </div>
                          )}
                          {doc.mimeType === 'application/pdf' && (
                            <div className="bg-red-50 flex items-center justify-center py-6 border-b border-gray-100">
                              <div className="text-center">
                                <FileText className="w-10 h-10 text-red-400 mx-auto mb-1" />
                                <p className="text-xs text-red-600 font-medium">PDF</p>
                              </div>
                            </div>
                          )}
                          <div className="p-4 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                              {isImage(doc.mimeType)
                                ? <Image className="w-5 h-5 text-blue-500 flex-shrink-0" />
                                : <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />}
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-800 truncate">
                                  {doc.description || doc.fileName}
                                </p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  <span className="font-medium text-amber-600">
                                    {DOC_TYPE_LABEL[doc.documentType] ?? doc.documentType}
                                  </span>
                                  {' · '}{fmtFileSize(doc.fileSize)}
                                  {' · '}{fmtDate(doc.uploadedAt)}
                                </p>
                              </div>
                            </div>
                            <a href={doc.url} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-600 text-xs font-bold hover:bg-amber-100 transition-colors flex-shrink-0">
                              <ExternalLink className="w-3.5 h-3.5" /> Abrir
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex gap-3 flex-shrink-0 flex-wrap">
              <button onClick={() => setModalVer(false)}
                className="px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                Fechar
              </button>
              <button onClick={() => abrirEditar(selecionado)}
                className="px-4 py-3 rounded-xl border-2 border-amber-300 text-amber-600 hover:bg-amber-50 font-bold flex items-center gap-2">
                <Edit className="w-4 h-4" /> Editar
              </button>
              <button onClick={() => handleExcluir(selecionado)} disabled={salvando}
                className="px-4 py-3 rounded-xl border-2 border-red-300 text-red-600 hover:bg-red-50 font-bold flex items-center gap-2 disabled:opacity-60">
                <Trash2 className="w-4 h-4" /> Excluir
              </button>
              <div className="flex-1" />
              {selecionado.status === 'pending' && (<>
                <button onClick={() => { setModalRejeitar(selecionado); setModalVer(false); setMotivo(''); }}
                  className="px-4 py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 flex items-center gap-2">
                  <XCircle className="w-5 h-5" /> Rejeitar
                </button>
                <button onClick={() => handleAprovar(selecionado.id)} disabled={salvando}
                  className="px-4 py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 flex items-center gap-2 disabled:opacity-60">
                  {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                  Aprovar
                </button>
              </>)}
              {selecionado.status === 'approved' && selecionado.telefone && (
                <button onClick={() => handleWhatsApp(selecionado)}
                  className="px-4 py-3 rounded-xl font-bold flex items-center gap-2 text-white"
                  style={{ backgroundColor: '#25D366' }}>
                  <MessageSquare className="w-5 h-5" /> Notificar WhatsApp
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODAL EDITAR ✅ NOVO
      ══════════════════════════════════════════════════════════════════ */}
      {modalEditar && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">

            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-amber-500 to-orange-500 rounded-t-2xl flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Edit className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-white leading-none">Editar Solicitação</h3>
                    <p className="text-amber-100 text-sm mt-0.5 font-mono">{modalEditar.systemId}</p>
                  </div>
                </div>
                <button onClick={() => setModalEditar(null)}
                  className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">

              {/* Erro */}
              {erroEdit && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-semibold text-red-800">{erroEdit}</p>
                </div>
              )}

              {/* Dados Pessoais */}
              <div>
                <h4 className="flex items-center gap-2 text-xs font-black text-gray-400 uppercase tracking-widest mb-4">
                  <User className="w-3.5 h-3.5" /> Dados Pessoais
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className={labelCls}>Nome Completo *</label>
                    <input value={formEdit.nomeCompleto} onChange={handleEditChange('nomeCompleto')}
                      className={inputCls} placeholder="Nome completo" />
                  </div>
                  <div>
                    <label className={labelCls}>Data de Nascimento</label>
                    <input type="date" value={formEdit.dataNascimento} onChange={handleEditChange('dataNascimento')}
                      className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>CPF *</label>
                    <input value={formEdit.cpf} onChange={handleEditChange('cpf')}
                      className={inputCls} placeholder="Somente números" inputMode="numeric" />
                  </div>
                  <div>
                    <label className={labelCls}>CNPJ (opcional)</label>
                    <input value={formEdit.cnpj} onChange={handleEditChange('cnpj')}
                      className={inputCls} placeholder="Somente números" inputMode="numeric" />
                  </div>
                  <div>
                    <label className={labelCls}>E-mail *</label>
                    <input type="email" value={formEdit.email} onChange={handleEditChange('email')}
                      className={inputCls} placeholder="email@dominio.com" />
                  </div>
                  <div>
                    <label className={labelCls}>Telefone / WhatsApp</label>
                    <input type="tel" value={formEdit.telefone} onChange={handleEditChange('telefone')}
                      className={inputCls} placeholder="(00) 00000-0000" />
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div>
                <h4 className="flex items-center gap-2 text-xs font-black text-gray-400 uppercase tracking-widest mb-4">
                  <MapPin className="w-3.5 h-3.5" /> Endereço
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>CEP</label>
                    <input value={formEdit.cep} onChange={handleEditChange('cep')}
                      className={inputCls} placeholder="00000000" inputMode="numeric" />
                  </div>
                  <div>
                    <label className={labelCls}>Estado (UF)</label>
                    <select value={formEdit.estado} onChange={handleEditChange('estado')} className={inputCls}>
                      <option value="">Selecione</option>
                      {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2 grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className={labelCls}>Endereço</label>
                      <input value={formEdit.endereco} onChange={handleEditChange('endereco')}
                        className={inputCls} placeholder="Rua / Avenida" />
                    </div>
                    <div>
                      <label className={labelCls}>Número</label>
                      <input value={formEdit.numero} onChange={handleEditChange('numero')}
                        className={inputCls} placeholder="Nº" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Cidade</label>
                    <input value={formEdit.cidade} onChange={handleEditChange('cidade')}
                      className={inputCls} placeholder="Cidade" />
                  </div>
                  <div>
                    <label className={labelCls}>Complemento</label>
                    <input value={formEdit.complemento} onChange={handleEditChange('complemento')}
                      className={inputCls} placeholder="Apto / Bloco / Ref." />
                  </div>
                </div>
              </div>

              {/* Histórico Profissional */}
              <div>
                <h4 className="flex items-center gap-2 text-xs font-black text-gray-400 uppercase tracking-widest mb-4">
                  <FileText className="w-3.5 h-3.5" /> Histórico Profissional
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Descrição do histórico profissional</label>
                    <textarea value={formEdit.historicoProfissional} onChange={handleEditChange('historicoProfissional')}
                      rows={5} className={inputCls}
                      placeholder="Inclua datas e evolução desde o primeiro curso..." />
                  </div>
                  <div>
                    <label className={labelCls}>Link das redes sociais / portfólio</label>
                    <input value={formEdit.redesSociais} onChange={handleEditChange('redesSociais')}
                      className={inputCls} placeholder="Instagram, TikTok, portfólio..." />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex gap-3 flex-shrink-0">
              <button onClick={() => setModalEditar(null)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-all">
                Cancelar
              </button>
              <button onClick={handleSalvarEdicao} disabled={salvando}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-black hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                {salvando ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL REJEITAR */}
      {modalRejeitar && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black text-gray-900">Rejeitar Solicitação</h3>
                <p className="text-sm text-gray-500 mt-0.5">{modalRejeitar.nomeCompleto}</p>
              </div>
              <button onClick={() => setModalRejeitar(null)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">Motivo da Rejeição *</label>
              <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={4}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 resize-none text-gray-900 font-semibold"
                placeholder="Informe o motivo (será registrado)..." />
              <p className={`text-xs mt-1 text-right ${motivo.trim().length < 10 ? 'text-red-400' : 'text-gray-400'}`}>
                {motivo.trim().length}/10 mínimo
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button onClick={() => setModalRejeitar(null)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleRejeitar} disabled={salvando}
                className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-60">
                {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <XCircle className="w-5 h-5" />}
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Sub-componentes ──────────────────────────────────────────────────────────
const Section: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div>
    <h4 className="flex items-center gap-2 text-sm font-black text-gray-500 uppercase tracking-wider mb-3">{icon} {title}</h4>
    {children}
  </div>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4">{children}</div>
);

const Info: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div>
    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
    <p className={`font-bold text-gray-900 text-sm ${mono ? 'font-mono' : ''}`}>{value}</p>
  </div>
);

export default GerenciamentoRevalida;